import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from 'state/axios';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import { 
    Box, 
    Typography, 
    TextField, 
    Button,  
    CircularProgress, 
    FormControlLabel, 
    Checkbox, 
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import Right from "./Right";


const PermissionPage = () => {
    useAuthCheck();
    const navigate = useNavigate();
    const { permissionId } = useParams();    

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    const [formValues, setFormValues] = useState({
        element: '',
        path: '',
        description: '',
        category: '',
        sortOrder: 0,
        isActive: true,
        isInvisible: false,
        right: [],
    });

    useEffect(() => {
        const fetchPermission = async () => {
            try {
                setLoading(true);

                if (permissionId) {
                    const url = `/permissions/${permissionId}`;
                    const response = await api.get(url);

                    const formData = response?.data || {};
                    setFormValues({
                        element: formData.element || '',
                        path: formData.path || '',
                        description: formData.description || '',
                        category: formData.category || '',
                        sortOrder: formData.sortOrder || 0,
                        isActive: formData.isActive ?? true,
                        isInvisible: formData.isInvisible ?? false,
                        right: formData.right || [],
                    })
                }
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };

        fetchPermission();
    }, [permissionId]);

    const checkoutSchema = yup.object().shape({
        element: yup.string().required('Element is required.'),
        path: yup.string().required('Path is required.'),
        category: yup.string().required('Category is required.'),
        sortOrder: yup
            .number()
            .min(0, 'Sort order must be 0 or greater.')
            .required('Sort order is required.'),
    });

    const handleFormSubmit = async (values, onSubmitProps) => {
        try {
            const method = permissionId ? "PUT" : "POST";
            const url = permissionId
                ? `/permissions/update/${permissionId}`
                : '/permissions/new';            
            const payload = values;

            await api({
                method,
                url,
                data: payload,
            });

            toast.success(
                permissionId 
                    ? "Permission updated successfully." 
                    : "Permission created successfully."
            );

            // onSubmitProps.resetForm(); 
            navigate("/permissions");
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to update permission!");
            setError(error);
        }
    };


    return (
        <Box className="main-container-box">                
            <FlexBetween>
                <Header
                    title="Permission"
                    subtitle={`${loading ? 'Loading...' : permissionId ? 'Update' : 'Create'}`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <StyledButton 
                            onClick={() => navigate("/permissions")}
                        >
                            <ArrowBackIosNewIcon />
                        </StyledButton>
                    </Box>
                )}
            </FlexBetween>
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (                
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                    <Formik
                        initialValues={formValues}
                        enableReinitialize={true}
                        validationSchema={checkoutSchema}
                        onSubmit={handleFormSubmit}
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleBlur,
                            handleChange,
                            handleSubmit,
                            setFieldValue,
                        }) => (
                            <form onSubmit={handleSubmit}>
                                <Box
                                    display="grid"
                                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                    gap={2}
                                >
                                    <TextField
                                        fullWidth
                                        required
                                        variant="filled"
                                        type="text"
                                        label="Element"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.element}
                                        name="element"
                                        error={!!touched.element && !!errors.element}
                                        helperText={touched.element && errors.element}
                                        sx={{ gridColumn: 'span 6' }}
                                    />

                                    <TextField
                                        fullWidth
                                        required
                                        variant="filled"
                                        type="text"
                                        label="Path"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.path}
                                        name="path"
                                        error={!!touched.path && !!errors.path}
                                        helperText={touched.path && errors.path}
                                        sx={{ gridColumn: 'span 6' }}
                                    />

                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        multiline
                                        rows={1}
                                        label="Description"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.description}
                                        name="description"
                                        error={!!touched.description && !!errors.description}
                                        helperText={touched.description && errors.description}
                                        sx={{ gridColumn: 'span 12' }}
                                    />

                                    <TextField
                                        fullWidth
                                        required
                                        variant="filled"
                                        type="text"
                                        label="Category"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.category}
                                        name="category"
                                        error={!!touched.category && !!errors.category}
                                        helperText={touched.category && errors.category}
                                        sx={{ gridColumn: 'span 6' }}
                                    />
                                    
                                    <TextField
                                        fullWidth
                                        required
                                        variant="filled"
                                        type="number"
                                        label="Sort Order"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.sortOrder}
                                        name="sortOrder"
                                        error={!!touched.sortOrder && !!errors.sortOrder}
                                        helperText={touched.sortOrder && errors.sortOrder}
                                        slotProps={{
                                            htmlInput: {
                                                min: 0,
                                                onKeyDown: (e) => {
                                                    if (e.key === '-' || e.key === 'e') e.preventDefault();
                                                },
                                            },
                                        }}
                                        sx={{ gridColumn: 'span 6' }}
                                    />

                                    <FormControlLabel
                                        sx={{ gridColumn: 'span 6' }}
                                        control={
                                            <Checkbox
                                                checked={values.isActive}
                                                onChange={handleChange}
                                                name="isActive"
                                                color="primary"
                                            />
                                        }
                                        label="Is Active"
                                    />

                                    <FormControlLabel
                                        sx={{ gridColumn: 'span 6' }}
                                        control={
                                            <Checkbox
                                                checked={values.isInvisible}
                                                onChange={handleChange}
                                                name="isInvisible"
                                                color="primary"
                                            />
                                        }
                                        label="Is Invisible"
                                    />

                                    <Typography variant="h6" fontWeight="bold" sx={{ gridColumn: 'span 12' }}>
                                        Right
                                    </Typography>                                        
                                    <Box sx={{ gridColumn: 'span 12', ml: 2 }}>
                                        <Right
                                            values={values}
                                            setFieldValue={setFieldValue}
                                        />
                                    </Box>
                                    
                                    <Button
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        sx={{ gridColumn: "span 12", height: 42 }}
                                    >
                                        {permissionId ? "Update" : "Create"}
                                    </Button>
                                </Box>
                            </form>
                        )}
                    </Formik>
                </Box>
            )}
        </Box>
    );
};

export default PermissionPage;
